import {
	Circle,
	MapContainer,
	Marker,
	Polygon,
	Popup,
	TileLayer,
	Tooltip,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-routing-machine/dist/leaflet-routing-machine.css";
import { Button } from "@components";
import type { Map as LeafletMap } from "leaflet";
import L from "leaflet";
import "leaflet-routing-machine";
import { Hexagon01 } from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { CarIcon } from "@/components/ui/icons/CarIcon";
import { cx } from "@/utils/cx";
import {
	AddressSearchInput,
	ClusteredVehicles,
	DrawingControls,
	DrawingHandler,
	FenceDataModal,
	FencesList,
	MapController,
	RoutePlaybackBar,
	RoutePlaybackPositionPopup,
	RoutePointCard,
	RouteWithDirection,
	SideModal,
	useCompleteDrawing,
} from "../components";
import { VehicleList } from "../components/vehicle-list";
import { useTrackingVehicles } from "../hooks";
import { useFences } from "../hooks/use-fences";
import { useInvalidateMapSizeOnPanelToggle } from "../hooks/use-invalidate-map-size-on-panel-toggle";
import { useMapFocusOnSelectedFence } from "../hooks/use-map-focus-on-selected-fence";
import { useRouteSelection } from "../hooks/use-route-selection";
import { useTrackingStore } from "../store";
import { useFenceStore } from "../store/fence-store";
import type { RouteMarker, Telemetry } from "../types";
import { parseCircleFromWKT, parsePolygonFromWKT } from "../utils/fence.utils";
import { formatTime } from "../utils/tracking.utils";

const renderRouteMarkerPopup = (marker: NonNullable<RouteMarker>) => (
	<Popup className="custom-popup" maxHeight={180}>
		<RoutePointCard
			data={{
				latitude: String(marker.position[0]),
				longitude: String(marker.position[1]),
				speed: marker.speed ?? 0,
				dateTelemetry: marker.dateTelemetry ?? "",
			}}
			style={{ padding: 0, background: "none", border: "none" }}
		/>
	</Popup>
);

const TrackingPage = () => {
	const {
		isVehiclesModalOpen,
		routeCoords,
		routeStartMarker,
		routeEndMarker,
		positionMarkers,
		selectedPositionInfo,
		selectedPositionIndex,
		vehicleFilterId,
		openVehiclesModal,
		closeVehiclesModal,
		setSelectedVehicleId,
		setVehicleFilterId,
		clearRoute,
	} = useTrackingStore();

	const {
		isFencesModalOpen,
		drawingMode,
		showFencesOnMap,
		selectedFence,
		openFencesModal,
		closeFencesModal,
	} = useFenceStore();

	const { handleComplete, handleCancel } = useCompleteDrawing();

	const { data: fencesData } = useFences({ pageSize: 100 });

	const mapRef = useRef<LeafletMap | null>(null);
	const [searchParams] = useSearchParams();
	const routingControlRef = useRef<L.Control | null>(null);
	const markerRefs = useRef<(L.Marker | null)[]>([]);

	const initialRouteVehicleId = useMemo(() => {
		const raw = searchParams.get("vehicleId");
		if (!raw) {
			return null;
		}
		const parsed = Number.parseInt(raw, 10);
		return Number.isNaN(parsed) ? null : parsed;
	}, [searchParams]);
	const initialTripIds = useMemo(() => {
		const raw = searchParams.get("tripIds");
		if (!raw) {
			return [];
		}
		return raw
			.split(",")
			.map((value) => Number.parseInt(value.trim(), 10))
			.filter((value) => Number.isInteger(value) && value > 0);
	}, [searchParams]);

	useEffect(() => {
		if (!initialRouteVehicleId) {
			return;
		}
		openVehiclesModal();
	}, [initialRouteVehicleId, openVehiclesModal]);

	useEffect(() => {
		if (selectedPositionIndex !== null) {
			setTimeout(() => {
				if (markerRefs.current[selectedPositionIndex]) {
					markerRefs.current[selectedPositionIndex]?.openPopup();
				}
			}, 100);
		}
	}, [selectedPositionIndex]);

	useMapFocusOnSelectedFence(selectedFence, mapRef);
	useInvalidateMapSizeOnPanelToggle(
		mapRef,
		isVehiclesModalOpen,
		isFencesModalOpen
	);

	const { data: trackingData, isLoading } = useTrackingVehicles();

	const removeRoutingControl = useCallback(() => {
		if (!(routingControlRef.current && mapRef.current)) {
			return;
		}

		mapRef.current.removeControl(routingControlRef.current);
		routingControlRef.current = null;
	}, []);

	const {
		replayPoints,
		routePlayback,
		handleRouteSelect,
		handleClosePlayback,
		clearReplay,
	} = useRouteSelection(mapRef, removeRoutingControl);

	const handleVehicleSelect = useCallback(
		(telemetry: Telemetry) => {
			if (!(telemetry.latitude && telemetry.longitude)) {
				return;
			}

			removeRoutingControl();

			clearRoute();
			clearReplay();
			setSelectedVehicleId(telemetry.vehicleId);
			setVehicleFilterId(telemetry.vehicleId);

			if (mapRef.current) {
				const map = mapRef.current;
				map.setView(
					[
						Number.parseFloat(telemetry.latitude),
						Number.parseFloat(telemetry.longitude),
					],
					16
				);
			}
		},
		[
			setSelectedVehicleId,
			setVehicleFilterId,
			removeRoutingControl,
			clearRoute,
			clearReplay,
		]
	);

	const handleVehicleListSelect = useCallback(
		(telemetry: Telemetry) => {
			if (!(telemetry.latitude && telemetry.longitude)) {
				return;
			}

			if (vehicleFilterId === telemetry.vehicleId) {
				setVehicleFilterId(null);
				setSelectedVehicleId(null);
				return;
			}

			setVehicleFilterId(telemetry.vehicleId);
			handleVehicleSelect(telemetry);
		},
		[
			vehicleFilterId,
			setVehicleFilterId,
			setSelectedVehicleId,
			handleVehicleSelect,
		]
	);

	const handleOpenVehiclesModal = useCallback(() => {
		setSelectedVehicleId(null);
		setVehicleFilterId(null);
		openVehiclesModal();
	}, [setSelectedVehicleId, setVehicleFilterId, openVehiclesModal]);

	const handleCloseVehiclesModal = useCallback(() => {
		removeRoutingControl();
		clearRoute();
		clearReplay();
		setSelectedVehicleId(null);
		setVehicleFilterId(null);
		closeVehiclesModal();
	}, [
		closeVehiclesModal,
		removeRoutingControl,
		clearRoute,
		clearReplay,
		setSelectedVehicleId,
		setVehicleFilterId,
	]);

	const handleAddressSelect = useCallback((lat: number, lon: number) => {
		if (mapRef.current) {
			mapRef.current.setView([lat, lon], 16);
		}
	}, []);

	useEffect(() => {
		return () => {
			clearReplay();
			if (routingControlRef.current && mapRef.current) {
				try {
					mapRef.current.removeControl(routingControlRef.current);
				} catch {
					//
				}
				routingControlRef.current = null;
			}
		};
	}, [clearReplay]);

	return (
		<div className="flex h-screen flex-col gap-6 px-6 py-8">
			<div className="flex items-start justify-between">
				<h1 className="font-semibold text-2xl text-primary leading-8">
					Rastreamento
				</h1>

				<div className="flex gap-2">
					<Button onClick={openFencesModal} variant="secondary">
						<Hexagon01 />
						Cercas
					</Button>
					<Button onClick={handleOpenVehiclesModal}>
						<CarIcon />
						Veiculos
					</Button>
				</div>
			</div>

			<div
				className={cx(
					"relative z-10 flex-1 transition-[margin] duration-300",
					isVehiclesModalOpen || isFencesModalOpen ? "mr-[400px]" : ""
				)}
			>
				<AddressSearchInput onLocationSelect={handleAddressSelect} />

				<style>{"img[src*='marker-icon'] { display: none !important; }"}</style>
				<MapContainer
					center={[-16.6799, -49.255]}
					className="h-full rounded-2xl"
					zoom={13}
				>
					<TileLayer
						attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					/>

					<MapController mapRef={mapRef} />

					<DrawingHandler mapRef={mapRef} />

					{!isLoading &&
						trackingData?.vehicles &&
						routeCoords.length === 0 &&
						positionMarkers.length === 0 && (
							<ClusteredVehicles
								onVehicleSelect={handleVehicleSelect}
								telemetries={trackingData.vehicles}
							/>
						)}

					{routeCoords.length > 0 && (
						<RouteWithDirection points={routeCoords} />
					)}

					{replayPoints !== null && routePlayback.currentPosition !== null ? (
						<Marker
							icon={L.divIcon({
								html: `<img src="/tracking/card-moving.svg" style="width:32px;height:32px;filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3)); transform: rotate(${routePlayback.currentPosition.bearing}deg);" />`,
								className: "route-playback-marker",
								iconAnchor: [16, 16],
								iconSize: [32, 32],
							})}
							position={[
								routePlayback.currentPosition.lat,
								routePlayback.currentPosition.lng,
							]}
						>
							<Popup
								className="custom-popup"
								closeButton={false}
								maxWidth={360}
							>
								<RoutePlaybackPositionPopup
									distanceKm={routePlayback.currentPosition.distanceKm}
									speed={routePlayback.currentPosition.speed}
									timestamp={routePlayback.currentPosition.timestamp}
								/>
							</Popup>
						</Marker>
					) : null}

					{!!routeStartMarker &&
						(routeStartMarker.showCar ? (
							<Marker
								icon={L.divIcon({
									html: `<img src="/tracking/card-moving.svg" width="32" height="32" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));" />`,
									className: "route-marker",
									iconSize: [32, 32],
									iconAnchor: [16, 16],
								})}
								position={routeStartMarker.position}
							>
								{renderRouteMarkerPopup(routeStartMarker)}
							</Marker>
						) : (
							<Marker
								icon={L.divIcon({
									html: `<div style="background-color: #1849A9; width: 18px; height: 18px; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
									className: "route-marker",
									iconSize: [16, 16],
									iconAnchor: [8, 8],
								})}
								position={routeStartMarker.position}
							>
								{renderRouteMarkerPopup(routeStartMarker)}
							</Marker>
						))}

					{!!routeEndMarker && (
						<Marker
							icon={L.divIcon({
								html: `<div style="background-color: #1849A9; width: 18px; height: 18px; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
								className: "route-marker",
								iconSize: [16, 16],
								iconAnchor: [8, 8],
							})}
							position={routeEndMarker.position}
						>
							{renderRouteMarkerPopup(routeEndMarker)}
						</Marker>
					)}

					{positionMarkers.map((marker, index) => (
						<Marker
							icon={L.divIcon({
								html: `<img src="/tracking/map-marker-red.svg" width="20" height="26" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3)); cursor: pointer;" />`,
								className: "position-marker",
								iconSize: [36, 36],
								iconAnchor: [10, 26],
							})}
							key={marker.id}
							position={marker.position}
							ref={(el) => {
								markerRefs.current[index] = el;
							}}
						>
							{selectedPositionInfo ? (
								<Popup>
									<div className="min-w-[150px] text-sm">
										<p className="font-bold text-gray-800">
											{selectedPositionInfo.time}
										</p>
										<p className="text-gray-600">
											Motorista: {selectedPositionInfo.driver}
										</p>
										<p className="text-gray-600">
											Velocidade: {selectedPositionInfo.speed} km/h
										</p>
									</div>
								</Popup>
							) : null}
						</Marker>
					))}

					{Boolean(showFencesOnMap) &&
						fencesData?.items?.map((fence) => {
							const isSelected = selectedFence?.id === fence.id;
							const fenceColor = isSelected ? "#2E90FA" : "#2E90FA";

							if (fence.type === 1) {
								const circle = parseCircleFromWKT(fence.area);
								if (!circle) {
									return null;
								}
								return (
									<Circle
										center={circle.center}
										key={`circle-${fence.id}-${fence.updatedAt}`}
										pathOptions={{
											color: fenceColor,
											fillColor: fenceColor,
											fillOpacity: 0.2,
											weight: isSelected ? 3 : 2,
										}}
										radius={circle.radius}
									>
										<Tooltip
											className="custom-fence-tooltip"
											direction="center"
											permanent
										>
											{fence.name}
										</Tooltip>
									</Circle>
								);
							}
							const polygon = parsePolygonFromWKT(fence.area);

							if (!polygon) {
								return null;
							}

							return (
								<Polygon
									key={`polygon-${fence.id}-${fence.updatedAt}`}
									pathOptions={{
										color: fenceColor,
										fillColor: fenceColor,
										fillOpacity: 0.2,
										weight: isSelected ? 3 : 2,
									}}
									positions={polygon.points}
								>
									<Tooltip
										className="custom-fence-tooltip"
										direction="center"
										permanent
									>
										{fence.name}
									</Tooltip>
								</Polygon>
							);
						})}
				</MapContainer>

				{replayPoints !== null && replayPoints.length > 1 ? (
					<RoutePlaybackBar
						elapsedMs={routePlayback.elapsedMs}
						endLabel={formatTime(replayPoints.at(-1)?.dateTelemetry ?? "")}
						isPlaying={routePlayback.isPlaying}
						onClose={handleClosePlayback}
						onSeek={routePlayback.seekToProgress}
						onSpeedChange={routePlayback.setSpeed}
						onToggle={routePlayback.toggle}
						overSpeedMarkers={routePlayback.overSpeedMarkers}
						progress={routePlayback.progress}
						speed={routePlayback.speed}
						startLabel={formatTime(replayPoints[0].dateTelemetry ?? "")}
					/>
				) : null}

				{drawingMode ? (
					<DrawingControls
						onCancel={handleCancel}
						onComplete={handleComplete}
					/>
				) : null}

				<SideModal
					isOpen={isVehiclesModalOpen}
					onClose={handleCloseVehiclesModal}
					title="Veículos"
				>
					<VehicleList
						initialRouteVehicleId={initialRouteVehicleId}
						initialTripIds={initialTripIds}
						onRouteSelect={handleRouteSelect}
						onVehicleSelect={handleVehicleListSelect}
					/>
				</SideModal>

				<SideModal
					isOpen={isFencesModalOpen}
					onClose={closeFencesModal}
					title="Cercas"
					width="400px"
				>
					<FencesList />
				</SideModal>

				<FenceDataModal />
			</div>
		</div>
	);
};

export default TrackingPage;
